import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type AreaType = 'tickets' | 'purchases' | 'projects' | 'changes';

interface AreaContextType {
  area: AreaType;
  currentArea: AreaType;
  setArea: (area: AreaType) => void;
}

const AreaContext = createContext<AreaContextType | undefined>(undefined);

const AREA_STORAGE_KEY = 'suportnect_current_area';

export const AreaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [area, setAreaState] = useState<AreaType>(() => {
    // Tentar carregar do localStorage
    const stored = localStorage.getItem(AREA_STORAGE_KEY);
    if (stored === 'tickets' || stored === 'purchases' || stored === 'projects' || stored === 'changes') {
      return stored as AreaType;
    }
    // Padrão: tickets
    return 'tickets';
  });

  // Persistir no localStorage quando mudar
  useEffect(() => {
    localStorage.setItem(AREA_STORAGE_KEY, area);
  }, [area]);

  // Se usuário perdeu permissão, não manter área "projects" salva
  useEffect(() => {
    if (!user) return;
    const canProjects = user.role === 'admin' || user.can_access_projects;
    if (area === 'projects' && !canProjects) {
      setAreaState('tickets');
    }
  }, [user, area]);

  // Tema visual da área (modo escuro compras ↔ dashboard): variáveis CSS em App.css / Dashboard.css
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-app-area', area);
  }, [area]);

  const setArea = useCallback((newArea: AreaType) => {
    if (newArea === 'projects') {
      const u = user;
      if (u && u.role !== 'admin' && !u.can_access_projects) {
        return;
      }
    }
    setAreaState(newArea);
  }, [user]);

  return (
    <AreaContext.Provider value={{ area, currentArea: area, setArea }}>
      {children}
    </AreaContext.Provider>
  );
};

export const useArea = () => {
  const context = useContext(AreaContext);
  if (context === undefined) {
    throw new Error('useArea must be used within an AreaProvider');
  }
  return context;
};
